import { useCallback, useEffect, useMemo } from 'react'
import { usePersistentState } from '@/hooks/use-persistent-state'
import { useUser } from '@/contexts/UserContext'
import { WHATS_NEW, type WhatsNewEntry } from '@/config/whatsNew'

/**
 * What this person has not been told yet.
 *
 * "Seen" is the id of the newest entry they have been shown. Everything above it in WHATS_NEW is
 * unseen. There are no dates to compare, and none are needed: the array order is the chronology.
 *
 *
 * THE STORAGE KEY IS NAMESPACED BY USER, AND THAT MATTERS
 *
 * `useGlobalAnnouncements` stores its dismissals under a bare `gracklecrm_dismissed_announcements`
 * with no user in the key, so on a shared browser one person dismissing a banner hides it from
 * everybody who logs in after them. That is the mistake this must not copy.
 *
 * `usePersistentState` behaves as plain `useState` when handed `undefined`, so nothing is written
 * before the user resolves and a signed-out render cannot land in somebody else's slot.
 *
 *
 * ONE HOOK, ONE STORED VALUE
 *
 * Deliberately not split into a reader and a baseline-setter. Two `usePersistentState` calls on
 * the same key produce two independent pieces of React state - writing through one would leave
 * the other stale until a reload, and the bug would look like the dialog reappearing at random.
 *
 *
 * TWO CASES THAT BOTH RESOLVE TO "SHOW NOTHING"
 *
 * No marker at all is somebody who has never seen the dialog, which on the day this ships is
 * everybody. Showing them all seventy entries would be absurd, so the first encounter quietly
 * records the newest as seen and shows nothing: a person new to the CRM does not want a
 * changelog, they want the application.
 *
 * A marker matching no entry means an id was changed or an entry removed. Rather than read an
 * unrecognised marker as "has seen nothing" and dump the lot, it resolves the same way. Failing
 * quiet is right, because the loud failure is seventy updates in somebody's face.
 */

/** Enough to be worth reading. Beyond this the dialog points at the full list in Help. */
const LIMIT = 8

export interface WhatsNew {
  /** Entries newer than this person's marker, newest first, capped at LIMIT. */
  unseen: WhatsNewEntry[]
  /** How many are actually unseen, before the cap. */
  unseenCount: number
  /** Whether there is something worth interrupting this person for. */
  shouldShow: boolean
  /** Record everything current as seen. Called when the dialog closes. */
  markAllSeen: () => void
}

export function useWhatsNew(): WhatsNew {
  const { currentUser } = useUser()
  const newest = WHATS_NEW[0]?.id

  const storageKey = currentUser?.id ? `gracklecrm_whatsnew_seen_${currentUser.id}` : undefined
  const [lastSeenId, setLastSeenId] = usePersistentState<string | null>(storageKey, null)

  const markAllSeen = useCallback(() => {
    if (newest) setLastSeenId(newest)
  }, [newest, setLastSeenId])

  /*
   * Set a baseline when the marker is absent OR unusable.
   *
   * The absent case is a first encounter, and must not show a backlog: every existing user has
   * no marker the day this ships, and reading that as "has seen nothing" would greet them with
   * seventy entries.
   *
   * The unusable case is a marker naming an entry that no longer exists. It must be RESET, not
   * merely ignored: an unrecognised marker is truthy, so leaving it in place means it is still
   * unrecognised on the next render, and the next, and that person never sees the dialog again.
   * A test caught exactly that - the quiet version of this bug is a user who silently stops
   * being told anything.
   */
  const resolves = Boolean(lastSeenId) && WHATS_NEW.some((e) => e.id === lastSeenId)
  useEffect(() => {
    if (storageKey && !resolves && newest) setLastSeenId(newest)
  }, [storageKey, resolves, newest, setLastSeenId])

  return useMemo(() => {
    const nothing = {
      unseen: [] as WhatsNewEntry[],
      unseenCount: 0,
      shouldShow: false,
      markAllSeen,
    }

    // Not signed in, or the user has not resolved yet: decide nothing.
    if (!storageKey) return nothing
    // No marker: the effect above is setting the baseline.
    if (!lastSeenId) return nothing

    const index = WHATS_NEW.findIndex((e) => e.id === lastSeenId)
    // Marker points at an entry that no longer exists.
    if (index === -1) return nothing

    const all = WHATS_NEW.slice(0, index)
    return {
      unseen: all.slice(0, LIMIT),
      unseenCount: all.length,
      shouldShow: all.length > 0,
      markAllSeen,
    }
  }, [storageKey, lastSeenId, markAllSeen])
}
